import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Virtuoso } from 'react-virtuoso';
import { DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Truncated } from '@frontend/design-system';
import { TableActions } from '../../../table-actions';
import { useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';

interface Props {
  isDemoAccount?: boolean;
}

export const NSRRoiTopRepeatComparisonChart = ({ isDemoAccount }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const { isLoading, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.APPOINTMENTS_ALL_TIME_PERSONS,
    },
    filters,
  });

  const sortedPersons = useMemo(() => {
    return rawData?.data?.aggregates
      ? // Sort by value in descending order, when multiple persons have same value, then sort by no show rate count
        [...rawData.data.aggregates].sort((a, b) => {
          if ((b.value || 0) === (a.value || 0)) {
            return (b.noShowRateCount || 0) - (a.noShowRateCount || 0);
          }
          return (b.value || 0) - (a.value || 0);
        })
      : // [...rawData.data.aggregates].sort((a, b) => (b.value || 0) - (a.value || 0))
        [];
  }, [rawData]);

  return (
    <Chart css={styles.chart} isLoading={isLoading}>
      <Chart.Header
        title={t('Top Repeat No-Show Patients - Last 12 Months')}
        infoTip={
          <InfoTipPopover>
            {t('Identify patients with frequent repeat no-shows to reduce missed appointments')}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin
      />
      {sortedPersons.length ? (
        <div css={styles.listWrapper}>
          <Virtuoso
            computeItemKey={(index, item) => item.name ?? index}
            data={sortedPersons}
            itemContent={(_index, { id, name, nextAppointmentDate, noShowRateCount, number, value = 0 }) => {
              if (!id) return null;

              return (
                <RowItem
                  key={id}
                  nextDate={nextAppointmentDate}
                  noShowCount={noShowRateCount}
                  noShowRate={Math.round(value)}
                  personId={id}
                  personName={name}
                  phoneNumber={number}
                />
              );
            }}
            totalCount={sortedPersons.length}
          />
        </div>
      ) : (
        <div css={styles.emptyWrapper}>
          <EmptyStates
            emptyStateConfig={{
              header: '',
              styles: styles.emptyState,
              type: 'analytics',
            }}
          />
        </div>
      )}
    </Chart>
  );
};

interface RowItemProps {
  isDemoAccount?: boolean;
  nextDate?: string;
  noShowCount?: number;
  noShowRate?: number;
  personId: string;
  personName?: string;
  phoneNumber?: string;
  warning?: boolean;
}

const RowItem = ({
  isDemoAccount,
  nextDate,
  noShowCount = 0,
  noShowRate = 0,
  personId,
  personName,
  phoneNumber,
}: RowItemProps) => {
  const { t } = useTranslation('analytics');

  if (!personId) return null;

  return (
    <div css={styles.item}>
      <div className='person-details'>
        <Truncated className='person-name'>{personName}</Truncated>
        <Truncated className='no-show-details'>
          {t('{{noShowCount}} No-Shows | {{noShowRate}}%', { noShowCount, noShowRate })}
        </Truncated>
      </div>

      <div className='next-appointment'>
        <Icon color='subdued' name='clock-small' />
        <Truncated className='no-show-details'>
          {t('Next: {{date}}', { date: nextDate ? formatters.date(nextDate) : t('N/A') })}
        </Truncated>
      </div>

      <div css={styles.actionsWrapper}>
        {!!phoneNumber && (
          <TableActions
            isDemoAccount={isDemoAccount}
            personId={personId}
            personName={personName}
            size='small'
            phoneNumber={phoneNumber}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  emptyWrapper: css`
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: center;
  `,

  emptyState: css`
    height: 220px;
    justify-content: center;
    padding-top: ${theme.spacing(1.25)};
    padding: 0;

    > div:last-of-type {
      display: none;
    }
  `,

  listWrapper: css`
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    flex: 1;
    min-height: 320px;
    padding: ${theme.spacing(1, 0, 1, 1)};
  `,

  item: css`
    align-items: center;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.light};
    display: flex;
    gap: ${theme.spacing(2)};
    justify-content: space-between;
    margin: ${theme.spacing(1, 2, 1, 1)};
    overflow: hidden;
    padding: ${theme.spacing(1.5, 1.5, 1.5, 2)};
    position: relative;

    &:before {
      background-color: ${theme.colors.critical40};
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: ${theme.spacing(0.5)};
    }

    .person-details {
      max-width: calc((100% - 88px) / 2);
      width: 100%;
    }

    .person-name {
      font-size: ${theme.font.size.medium};
    }

    .no-show-details {
      font-size: ${theme.font.size.small};
      color: ${theme.font.colors.subdued};
    }

    .next-appointment {
      align-items: center;
      display: flex;
      gap: ${theme.spacing(0.5)};
      width: calc((100% - 88px) / 2);
    }
  `,

  actionsWrapper: css`
    display: flex;
    width: ${theme.spacing(7)};
    justify-content: flex-end;

    > button {
      height: ${theme.spacing(3)};
      margin-right: ${theme.spacing(1)};
      min-width: ${theme.spacing(3)};
      width: ${theme.spacing(3)};
    }
  `,

  chart: css`
    .chart-content {
      justify-content: space-between;
    }
  `,
};
